import React, { type ReactElement, useState } from 'react';

import type { TGlobalFunctionTyped } from 'common/hooks/useTranslation';
import { type AnalyticalFieldMapping } from 'modules/bookkeep/settings/accounting';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';

import { type ModalState } from './ModalState';
import { StaticMapAndPullAndMapTable } from './StaticMapAndPullAndMapTable';
import { StaticMapAndPullAndMapConfirmChangesModal } from './StaticMapAndPullSectionConfirmChagnesModal';
import { StaticMapAndPullAndMapDeleteModal } from './StaticMapAndPullSectionDeleteModal';
import { StaticMapAndPullAndMapModal } from './StaticMapAndPullSectionModal';
import {
  getAnalyticalFieldsHeaderI18Key,
  getCustomFieldsDescriptionI18nKey,
} from './translations';
import { useCustomFieldAndAnalyticalFieldQueries } from './useCustomFieldAndAnalyticalFieldQueries';
import { useFeature } from '../../../../../../../../../common/hooks/useFeature';
import FEATURES from '../../../../../../../../../constants/features';
import { type AccountingSoftware } from '../../../../../../../integration/status';
import { type AnalyticalFieldMappingUpdate } from '../../../../../hooks/useUpdateAnalyticalFieldMappingMutation';
import { AnalyticalFieldErrorWarring } from '../AnalyticalFieldErrorWarring';
import { AnalyticalFieldSkeletonTable } from '../AnalyticalFieldSkeletonTable';
import { StaticMapAndPullAndMapTableMapSync } from './StaticMapAndPullAndMapTableMapSync';

interface Props {
  integration: AccountingSoftware;
  onUpdate: (
    editedAnalyticalField: AnalyticalFieldMappingUpdate & {
      externalName: string;
    },
  ) => void;
}

export const StaticMapAndPullAndMapSection = ({
  integration,
  onUpdate,
}: Props) => {
  const { t } = useTranslation('global');

  const queriesState = useCustomFieldAndAnalyticalFieldQueries();

  const [modalState, setModalState] = useState<ModalState>({
    modalKind: 'closed',
  });

  const hasCostCenterFeature = useFeature(FEATURES.COST_CENTERS);

  const hasCostCenterActive = useFeature(FEATURES.COST_CENTERS_ACTIVATED);

  const hasCustomFieldsActive = useFeature(FEATURES.CUSTOM_FIELDS);

  const {
    isFieldMappingsLoading,
    analyticalFieldMappings,
    isMapSyncEnabled,
    isExportConfigLoading,
    exportConfig,
    isSyncSettingsLoading,
    allSyncSettings,
  } = useAnalyticalFieldMapping();

  const isMapSyncTableLoading =
    isFieldMappingsLoading ||
    isExportConfigLoading ||
    isSyncSettingsLoading ||
    queriesState.status === 'loading';

  return (
    <div id="vat-accounts">
      {!isMapSyncEnabled && (
        <>
          <h3 className="IntegrationAccountingPage__section-title heading-l">
            {t(getAnalyticalFieldsHeaderI18Key(integration))}
          </h3>
          <p className="IntegrationAccountingPage__section-description body-m">
            {t(getCustomFieldsDescriptionI18nKey(integration))}
            {renderHelpCenterLink(integration, t)}
          </p>
        </>
      )}

      {isMapSyncEnabled && (
        <>
          <h3 className="IntegrationAccountingPage__section-title heading-l mb-24">
            {t(
              'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.header',
            )}
          </h3>
          {isMapSyncTableLoading && <AnalyticalFieldSkeletonTable />}
          {!isMapSyncTableLoading && (
            <>
              <StaticMapAndPullAndMapTableMapSync
                analyticalFieldMapping={analyticalFieldMappings}
                exportConfig={exportConfig}
                allSyncSettings={allSyncSettings}
                customFields={
                  queriesState.status === 'success'
                    ? queriesState.data.customFields
                    : []
                }
                isCostCenterEnabled={
                  hasCostCenterFeature && hasCostCenterActive
                }
              />
            </>
          )}
        </>
      )}

      {!isMapSyncEnabled && (
        <QuerySuspense
          queryState={queriesState}
          loading={<AnalyticalFieldSkeletonTable />}
          fallback={() => <AnalyticalFieldErrorWarring />}
        >
          {(data) => {
            const onlyNotDeletedCustomFields = data.customFields.filter(
              (cf) => !cf.deleted_at,
            );

            const handleUpdate = () => {
              if (
                modalState.modalKind === 'map' &&
                modalState.kind === 'mapped'
              ) {
                const mappingThatUsesTheValueCurrently =
                  data.analyticalFieldMapping.find(
                    (mapping: AnalyticalFieldMapping) =>
                      mapping.kind === 'mapped' &&
                      mapping.codeType === modalState.codeType &&
                      (mapping.codeType !== 'customField' ||
                        (mapping.codeType === 'customField' &&
                          mapping.externalId &&
                          modalState.externalId &&
                          mapping.externalId === modalState.externalId)),
                  );

                if (mappingThatUsesTheValueCurrently) {
                  setModalState({
                    ...modalState,
                    modalKind: 'confirmChanges',
                    existingAnalyticalFiledMap: {
                      fieldName:
                        modalState.codeType === 'costCenter'
                          ? t(
                              'bookkeep.integrations.settings.analyticalFieldsMappingsTable.costCenters',
                            )
                          : t(
                              'bookkeep.integrations.settings.analyticalFieldsMappingsTable.customFields',
                            ),
                      mappedAnalyticalField:
                        mappingThatUsesTheValueCurrently.externalName,
                      newAnalyticalField: modalState.externalName,
                    },
                  });
                  return;
                }
                onUpdate(modalState);
                setModalState({ modalKind: 'closed' });
              }
            };
            return (
              <>
                <StaticMapAndPullAndMapTable
                  integration={integration}
                  setModalState={setModalState}
                  analyticalFieldMapping={data.analyticalFieldMapping}
                  customFields={onlyNotDeletedCustomFields}
                />

                <StaticMapAndPullAndMapModal
                  integration={integration}
                  customFields={onlyNotDeletedCustomFields}
                  modalState={modalState}
                  setModalState={setModalState}
                  hasCostCenterActive={hasCostCenterActive}
                  hasCostCenterFeature={hasCostCenterFeature}
                  hasCustomFieldsActive={hasCustomFieldsActive}
                  onUpdate={handleUpdate}
                />

                <StaticMapAndPullAndMapDeleteModal
                  integration={integration}
                  modalState={modalState}
                  setModalState={setModalState}
                  onUpdate={onUpdate}
                />

                <StaticMapAndPullAndMapConfirmChangesModal
                  modalState={modalState}
                  setModalState={setModalState}
                  onUpdate={onUpdate}
                />
              </>
            );
          }}
        </QuerySuspense>
      )}
    </div>
  );
};

function renderHelpCenterLink(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
): ReactElement {
  switch (integration) {
    case 'Datev':
      return (
        <>
          &nbsp;|&nbsp;
          <a
            href="https://helpcenter.spendesk.com/articles/5560143-how-to-set-up-datev-kostenstellen-mapping-bookkeep-2-0"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('misc.helpCenterArticle')}
          </a>
        </>
      );
    default:
      return <></>;
  }
}
